import React, { useState, useEffect } from 'react'
import * as c from 'common/c'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { Divider } from '@mui/material'
import { TEST_IDS } from '../../test-ids'
import {
  fetchOrders,
  removeOrder,
  clearError,
  editOrder,
} from '../../store/orders'
import { PlaceOrderDialog } from './place-order-dialog'
import { showModal, closeModal } from 'actions/app'
import { CommonSidebar, CommonSidebarLink } from '../common-sidebar'
import { type AppState } from 'state/index'

export function Orders() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [placeOrderOpen, setPlaceOrderOpen] = useState(false)
  const [editOrderOpen, setEditOrderOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)

  const orderState = useSelector((state: any) => state.orders)
  const permissions = useSelector(
    (state: AppState) => state.account.permissions,
  )
  const customClaims = useSelector(
    (state: AppState) => state.account.customClaims,
  )
  const isAdmin =
    permissions.includes('farmtrx:admin') || customClaims?.[c.FT_ADMIN_CLAIM]

  useEffect(() => {
    dispatch(fetchOrders({ page: page + 1, pageSize }) as any)
  }, [page, pageSize, dispatch])

  const handleEdit = (order: any) => {
    setSelectedOrder(order)
    setEditOrderOpen(true)
  }

  const handleDelete = (order: any) => {
    dispatch(
      showModal(
        <ConfirmDeleteOrder
          order={order}
          onConfirm={() => {
            dispatch(removeOrder(order.id) as any)
            dispatch(closeModal() as any)
          }}
          onCancel={() => {
            dispatch(closeModal() as any)
          }}
        />,
        { title: t('confirm_delete'), draggable: false },
      ) as any,
    )
  }

  const handleMarkInvoiced = (order: any) => {
    const today = new Date().toLocaleDateString()
    dispatch(editOrder({ id: order.id, data: { invoicedAt: today } }) as any)
  }

  const handleMarkShipped = (order: any) => {
    const today = new Date().toLocaleDateString()
    dispatch(editOrder({ id: order.id, data: { shippedAt: today } }) as any)
  }

  const getOrderStatus = (order: any) => {
    if (order.shippedAt) return t('order_shipped')
    if (order.invoicedAt) return t('order_invoiced')
    return t('order_pending')
  }

  const getProductsSummary = (orderDetails: any[]) => {
    if (!orderDetails || orderDetails.length === 0) return '-'
    return orderDetails
      .map((item) => `${item.quantity}x ${item.sku}`)
      .join(', ')
  }

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: t('date'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        return new Date(params.value).toLocaleDateString()
      },
    },
    {
      field: 'orderDetails',
      headerName: t('products'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return getProductsSummary(params.value)
      },
    },
    {
      field: 'description',
      headerName: t('description'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const orderDetails = params.row.orderDetails
        if (!orderDetails || orderDetails.length === 0) return '-'
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {orderDetails.map((item: any, index: number) => (
              <div key={index}>{item.name}</div>
            ))}
          </Box>
        )
      },
    },
    {
      field: 'total',
      headerName: t('total'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const orderDetails = params.row.orderDetails
        if (!orderDetails || orderDetails.length === 0) return '-'
        const total = orderDetails.reduce(
          (sum: number, item: any) =>
            sum + (item.unitPrice || 0) * item.quantity,
          0,
        )
        const currency = orderDetails[0]?.currency || 'USD'
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
        })
        return formatter.format(total)
      },
    },
    {
      field: 'status',
      headerName: t('status'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const order = params.row
        return getOrderStatus(order)
      },
    },
    {
      field: 'invoicedAt',
      headerName: t('invoiced_at'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? new Date(params.value).toLocaleDateString() : '-'
      },
    },
    {
      field: 'shippedAt',
      headerName: t('shipped_at'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? new Date(params.value).toLocaleDateString() : '-'
      },
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const order = params.row
        const canEdit = !order.invoicedAt || isAdmin
        const canDelete = !order.invoicedAt && !order.shippedAt

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {canEdit && (
              <Tooltip title={t('edit_order')}>
                <IconButton
                  size="small"
                  onClick={() => handleEdit(order)}
                  data-testid={TEST_IDS.editOrderButton}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t('delete_order')}>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(order)}
                  data-testid={TEST_IDS.deleteOrderButton}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            {isAdmin && !order.invoicedAt && (
              <Tooltip title={t('mark_invoiced')}>
                <IconButton
                  size="small"
                  onClick={() => handleMarkInvoiced(order)}
                  data-testid={TEST_IDS.markInvoicedButton}
                  color="primary"
                >
                  <CheckCircleIcon />
                </IconButton>
              </Tooltip>
            )}
            {isAdmin && !order.shippedAt && (
              <Tooltip title={t('mark_shipped')}>
                <IconButton
                  size="small"
                  onClick={() => handleMarkShipped(order)}
                  data-testid={TEST_IDS.markShippedButton}
                  color="primary"
                >
                  <LocalShippingIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )
      },
    },
  ]

  return (
    <Box sx={{ display: 'flex', width: 1, height: 1 }}>
      <CommonSidebar>
        <CommonSidebarLink
          to="/orders"
          icon={<ShoppingCartIcon />}
          label={t('orders')}
          data-testid={TEST_IDS.ordersNav}
        />
        <Divider />
      </CommonSidebar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          height: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <h1>{t('orders')}</h1>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setPlaceOrderOpen(true)}
            data-testid={TEST_IDS.placeOrderButton}
          >
            {t('place_order')}
          </Button>
        </Box>

        {orderState && orderState.error && (
          <Alert
            severity="error"
            onClose={() => dispatch(clearError())}
            sx={{ mb: 2 }}
          >
            {orderState.error}
          </Alert>
        )}

        {orderState?.loading && <CircularProgress />}

        {orderState && !orderState.loading && (
          <DataGrid
            rows={
              orderState.orders?.filter((order: any) => order && order.id) ?? []
            }
            columns={columns}
            getRowId={(row) => row.id}
            pagination
            paginationModel={{
              pageSize,
              page,
            }}
            onPaginationModelChange={(newModel) => {
              setPage(newModel.page)
              setPageSize(newModel.pageSize)
            }}
            pageSizeOptions={[10, 20, 50]}
            rowCount={orderState?.pagination?.total ?? 0}
            paginationMode="server"
            data-testid={TEST_IDS.ordersTable}
            getRowHeight={() => 'auto'}
            sx={{
              '& .MuiDataGrid-row': {
                height: 'auto',
              },
              '& .MuiDataGrid-cell': {
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflow: 'visible !important',
                lineHeight: '1.4rem',
                paddingTop: '12px',
                paddingBottom: '12px',
              },
            }}
          />
        )}

        <PlaceOrderDialog
          open={placeOrderOpen}
          onClose={() => setPlaceOrderOpen(false)}
          onOrderPlaced={() => {
            setPlaceOrderOpen(false)
            dispatch(fetchOrders({ page: 1, pageSize }) as any)
          }}
        />

        {selectedOrder && (
          <PlaceOrderDialog
            open={editOrderOpen}
            order={selectedOrder}
            isAdmin={isAdmin}
            onClose={() => {
              setEditOrderOpen(false)
              setSelectedOrder(null)
            }}
            onOrderUpdated={() => {
              setEditOrderOpen(false)
              setSelectedOrder(null)
              dispatch(fetchOrders({ page: page + 1, pageSize }) as any)
            }}
          />
        )}
      </Box>
    </Box>
  )
}

function ConfirmDeleteOrder({
  order,
  onConfirm,
  onCancel,
}: {
  order: any
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()

  return (
    <Box sx={{ p: 2 }}>
      <p>{t('delete_order_confirmation', { id: order.id })}</p>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="outlined" onClick={onCancel}>
          {t('cancel')}
        </Button>
        <Button variant="contained" color="error" onClick={onConfirm}>
          {t('delete')}
        </Button>
      </Box>
    </Box>
  )
}
